/**
 * Global Search Service
 * Handles searching across tasks, branches, and other entities
 */

import api from './api';

class SearchService {
  /**
   * Search across all entities (tasks, branches)
   * @param {string} query - Search query
   * @param {Object} options - Search options
   * @returns {Promise<Object>} Search results grouped by category
   */
  async globalSearch(query, options = {}) {
    if (!query || query.trim().length < 2) {
      return { tasks: [], branches: [] };
    }

    const { limit = 5 } = options;

    try {
      // Fetch tasks and branches in parallel
      const [tasksResult, branchesResult] = await Promise.allSettled([
        this.searchTasks(query, limit),
        this.searchBranches(query, limit),
      ]);

      return {
        tasks: tasksResult.status === 'fulfilled' ? tasksResult.value : [],
        branches: branchesResult.status === 'fulfilled' ? branchesResult.value : [],
      };
    } catch (error) {
      console.error('Global search error:', error);
      return { tasks: [], branches: [] };
    }
  }

  /**
   * Search tasks/jobs
   * @param {string} query - Search query
   * @param {number} limit - Max results
   * @returns {Promise<Array>} Matching tasks
   */
  async searchTasks(query, limit = 5) {
    try {
      const params = new URLSearchParams();
      params.append('search', query);
      params.append('limit', limit);

      const response = await api.get(`/job?${params.toString()}`);

      if (response.success && response.data) {
        const jobs = response.data.jobs || response.data;
        return Array.isArray(jobs) ? jobs.slice(0, limit) : [];
      }
      return [];
    } catch (error) {
      console.error('Task search error:', error);
      return [];
    }
  }

  /**
   * Search branches
   * @param {string} query - Search query
   * @param {number} limit - Max results
   * @returns {Promise<Array>} Matching branches
   */
  async searchBranches(query, limit = 5) {
    try {
      const params = new URLSearchParams();
      params.append('search', query);

      const response = await api.get(`/branch?${params.toString()}`);

      if (response.success || response.data) {
        const branches = response.data?.branches || response.data || [];
        return Array.isArray(branches) ? branches.slice(0, limit) : [];
      }
      return [];
    } catch (error) {
      console.error('Branch search error:', error);
      return [];
    }
  }
}

const searchService = new SearchService();
export default searchService;
